import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ClockIcon, LayersIcon, UsersIcon } from 'lucide-react';
import { Logo } from './Logo';
import { media } from '../data/chapters';
interface IntroScreenProps {
  onBegin: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
const META = [{
  icon: ClockIcon,
  label: '~60 seconds'
}, {
  icon: LayersIcon,
  label: '3 chapters'
}, {
  icon: UsersIcon,
  label: 'Peer comparison'
}];
export function IntroScreen({
  onBegin
}: IntroScreenProps) {
  return <div className="relative flex h-full flex-col overflow-y-auto bg-white no-scrollbar" data-id="element-398" data-mp="1imzhj4rfyilp">
      <div className="relative h-[44%] min-h-[300px] shrink-0 overflow-hidden bg-mist-100" data-id="element-399" data-mp="1we48ligqu2kf">
        <motion.img src={media.intro} alt="" initial={{
        scale: 1.15,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 2.2,
        ease: 'easeOut'
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-400" data-mp="1sen16jhravcm" />
        <div className="relative flex items-center justify-between px-6 pt-6 md:pt-11" data-id="element-401" data-mp="2ovgt1m6y6h8u">
          <div className="flex items-center gap-2.5" data-id="element-402" data-mp="2tt8i88g7jp9c">
            <Logo width={24} height={24} data-id="element-403" data-mp="2tu0dfi5w7kz3" />
            <span className="text-[13px] font-semibold tracking-[0.18em] text-navy-900" data-id="element-404" data-mp="2ysbr63zb6fml">IVF PULSE</span>
          </div>
          <span className="rounded-full border border-navy-900/10 bg-white/80 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-navy-700 backdrop-blur-md" data-id="element-405" data-mp="32vhryw3njelo">
            ESHRE 2027
          </span>
        </div>
      </div>

      <div className="relative flex flex-1 flex-col px-6 pb-7 pt-6" data-id="element-406" data-mp="3cqjhht1uaios">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-teal" data-id="element-407" data-mp="3hknqsiykv2t1">
          A peer perspective experience
        </motion.p>
        <motion.h1 initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5,
        ease
      }} className="mt-3 font-serif text-[30px] font-normal leading-[1.1] tracking-tight text-navy-900" data-id="element-408" data-mp="3vk5jvaiwp287">
          How closely does your approach align with <span className="italic text-pulse-teal" data-id="element-409" data-mp="2588qx54kuaqn">other fertility specialists?</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.7,
        ease
      }} className="mt-3 text-[15px] leading-relaxed text-slate-500" data-id="element-410" data-mp="20eecoh7qqnyy">
          Three short chapters on ovarian stimulation. Answer once, then see where you stand among your peers.
        </motion.p>

        <motion.ul initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9
      }} className="mt-4 flex flex-wrap gap-2" data-id="element-411" data-mp="2j670lx7ydizt">
          {META.map(({
          icon: Icon,
          label
        }) => <li key={label} className="flex items-center gap-1.5 rounded-full bg-mist-100 px-3 py-1.5 text-[12px] text-navy-700" data-id="element-412" data-mp="ou8sxzpakv61">
              <Icon className="h-3.5 w-3.5 text-pulse-teal" data-id="element-413" data-mp="35dt2gdgc8iem" />
              {label}
            </li>)}
        </motion.ul>

        <div className="mt-auto pt-6" data-id="element-414" data-mp="2rld59ta4belm">
          <motion.button type="button" onClick={onBegin} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 1,
          ease
        }} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-[15px] font-semibold text-white transition hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-teal focus-visible:ring-offset-2" data-id="element-415" data-mp="2voj6gmidifl8">
            Add your perspective
            <ArrowRightIcon className="h-4 w-4" data-id="element-416" data-mp="6xqevvux5dvj" />
          </motion.button>
          <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400" data-id="element-417" data-mp="az3mobhknr53">
            Concept prototype · Peer figures are illustrative · No data collected
          </p>
        </div>
      </div>
    </div>;
}